import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateItTicketData,
  ItTicketArchiveFilters,
  ItTicketBoardFilters,
  ItTicketStatus,
  itTicketsApi,
} from '@/services';

export const itTicketsKeys = {
  all: ['it-tickets'] as const,
  mine: ['it-tickets', 'mine'] as const,
  detail: (id: string) => ['it-tickets', 'detail', id] as const,
  board: (filters: ItTicketBoardFilters) => ['it-tickets', 'board', filters] as const,
  archive: (filters: ItTicketArchiveFilters) => ['it-tickets', 'archive', filters] as const,
  stats: (filters: ItTicketArchiveFilters) => ['it-tickets', 'stats', filters] as const,
};

export function useMyItTicketsQuery() {
  return useQuery({ queryKey: itTicketsKeys.mine, queryFn: () => itTicketsApi.listMine() });
}

export function useItTicketQuery(id: string, enabled = true) {
  return useQuery({ queryKey: itTicketsKeys.detail(id), queryFn: () => itTicketsApi.get(id), enabled });
}

export function useItTicketBoardQuery(filters: ItTicketBoardFilters = {}, enabled = true) {
  return useQuery({ queryKey: itTicketsKeys.board(filters), queryFn: () => itTicketsApi.board(filters), enabled });
}

export function useItTicketArchiveQuery(filters: ItTicketArchiveFilters, enabled = true) {
  return useQuery({ queryKey: itTicketsKeys.archive(filters), queryFn: () => itTicketsApi.archive(filters), enabled });
}

export function useItTicketStatsQuery(filters: ItTicketArchiveFilters, enabled = true) {
  return useQuery({ queryKey: itTicketsKeys.stats(filters), queryFn: () => itTicketsApi.stats(filters), enabled });
}

export function useCreateItTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateItTicketData) => itTicketsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}

export function useCommentItTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => itTicketsApi.comment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}

export function useUpdateItTicketStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: ItTicketStatus; comment?: string }) =>
      itTicketsApi.updateStatus(id, status, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}

export function useItTicketAssigneesQuery(enabled = true) {
  return useQuery({
    queryKey: [...itTicketsKeys.all, 'assignees'],
    queryFn: () => itTicketsApi.assignees(),
    enabled,
  });
}

export function useAssignItTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assigneeId }: { id: string; assigneeId: string | null }) => itTicketsApi.assign(id, assigneeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}

export function useUploadItTicketAttachmentsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, files }: { ticketId: string; files: File[] }) =>
      itTicketsApi.uploadAttachments(ticketId, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}

export function useDeleteItTicketAttachmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => itTicketsApi.deleteAttachment(attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: itTicketsKeys.all }),
  });
}
